import React, { useContext, useEffect, useMemo, useState } from "react";
import { Typography, Box, Grid2 as Grid } from "@mui/material";
import { collection, onSnapshot, orderBy, query, limit } from "firebase/firestore";

import { Context } from "../Context";
import { db } from "../firebase";
import { TrainPlate } from "./TrainPlate";
import { OccupancyState } from "./icons/Occupancy";

// What TrainPlate consumes. Derived per-render from RawRow + the clock.
type TrainItem = {
  lineId: string;          // e.g. "S1"
  routeId: string;         // e.g. "7459"
  active: boolean;
  lastUpdate: Date | null;
  occupancyLevel: OccupancyState | null;
};

// What we keep in state: the raw doc, NOT yet filtered for expiry. Expiry is a
// function of wall-clock time, so it cannot be decided in the snapshot callback
// — no snapshot fires when a train merely becomes stale. See `nowMs` below.
type RawRow = {
  lineId: string;
  routeId: string;
  status: string | null;      // sweep: ACTIVE | SCHEDULED | NO_DATA | ARRIVED | CANCELLED
  expiresMs: number | null;   // sweep-owned
  updatedMs: number | null;   // aggregator-owned
  occupancyLevel: OccupancyState | null;
  scheduledDeparture: string | null;
};

type RouteColumn = {
  route: string;      // lineId
  lines: TrainItem[]; // route docs
};

function toDateSafe(v: any): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const STALE_MS = 2 * 60 * 1000;

// Fallback only. Applies to legacy docs written by the aggregator before the
// sweep existed, which have `updatedAt` but no `expires_at`. Sweep-managed docs
// carry an explicit expiry and ignore this entirely.
const LIVE_MS = 15 * 60 * 1000; // 15 minutes

// How often to re-evaluate expiry. Independent of the sweep's 10s tick: this only
// needs to be fine enough that a train disappearing looks prompt.
const CLOCK_TICK_MS = 15 * 1000;

// Ordered by schedule, not recency. `updatedAt` cannot be used: Firestore drops
// documents missing the orderBy field, and the sweep deliberately does not write
// `updatedAt` (it belongs to the aggregator) — so ordering by it would hide every
// journey with no MGW feed, i.e. exactly the S1/S2 services this is meant to fix.
const ORDER_FIELD = "scheduled_departure_time";

// Was 30. The window is bounded by the sweep (~46 journeys across all lines at
// midday), but this is per-line and wants headroom for peak. Ascending schedule
// order means the limit trims the far future rather than an arbitrary slice.
const PER_LINE_LIMIT = 60;

// Visibility rule. At module scope deliberately: it closes over nothing, and a
// function declared in the component body would have to be a useMemo dependency.
//
// Two rules, in precedence order:
//   1. Sweep-managed doc (has expires_at) -> visible until that instant. The
//      sweep already applied the lead window and the status/linger model, so the
//      client does not second-guess it. This is also why the board keeps draining
//      if the sweep dies: trains leave on schedule and only "no new trains
//      appear" is lost.
//   2. Legacy doc (updatedAt only) -> the old recency rule, so anything the
//      aggregator writes for a journey the sweep has not seen still shows.
//
// A doc with neither is dropped, as before.
function isVisible(r: RawRow, now: number): boolean {
  if (r.expiresMs != null) return now < r.expiresMs;
  if (r.updatedMs != null) return now - r.updatedMs < LIVE_MS;
  return false;
}

function lineSortKey(id: string) {
  const t = id.trim().toUpperCase();
  const m = /^S(\d+)$/.exec(t);
  if (m) return parseInt(m[1], 10);
  return 9999;
}
function stableSecondaryKey(id: string) {
  return id.trim().toUpperCase();
}

const Sidebar: React.FC = () => {
  const { selected, setSelected } = useContext(Context);

  const [byLine, setByLine] = useState<Record<string, RawRow[]>>({});
  const [lineIds, setLineIds] = useState<string[]>([]);

  // Forces re-evaluation of expiry on a timer. Without this the board freezes
  // between snapshots: expiry passes with no Firestore event to trigger a render.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(t);
  }, []);

  // 1) Listen to lines (once)
  useEffect(() => {
    const qLines = query(collection(db, "lines"), orderBy("updatedAt", "desc"));

    const unsub = onSnapshot(
      qLines,
      (snap) => {
        const ids = snap.docs.map((d) => d.id);

        // keep only S-lines
        const filtered = ids.filter((id) => /^S\d+$/i.test(id.trim()));

        // stable ordering S1..S6..S60
        filtered.sort((a, b) => {
          const ka = lineSortKey(a);
          const kb = lineSortKey(b);
          if (ka !== kb) return ka - kb;
          return stableSecondaryKey(a).localeCompare(stableSecondaryKey(b));
        });

        setLineIds(filtered);
      },
      (err) => console.error("❌ lines onSnapshot error", err)
    );

    return () => unsub();
  }, []);

  // 2) For each line, listen to routes and populate byLine
  useEffect(() => {
    const unsubs: Array<() => void> = [];

    // wipe removed lines so UI doesn't show stale routes
    setByLine((prev) => {
      const next: Record<string, RawRow[]> = {};
      lineIds.forEach((id) => (next[id] = prev[id] ?? []));
      return next;
    });

    lineIds.forEach((lineId) => {
      const qRoutes = query(
        collection(db, "lines", lineId, "routes"),
        orderBy(ORDER_FIELD, "asc"),
        limit(PER_LINE_LIMIT)
      );

      const unsub = onSnapshot(
        qRoutes,
        (snap) => {
          // No expiry filtering here — see RawRow. This maps only.
          const rows: RawRow[] = snap.docs.map((d) => {
            const data: any = d.data();

            // Prefer unix_ms if present
            const updatedMs =
              typeof data?.updatedAt_unix_ms === "number"
                ? data.updatedAt_unix_ms
                : (() => {
                    const dt = toDateSafe(data?.updatedAt);
                    return dt ? dt.getTime() : null;
                  })();

            const expiresMs =
              typeof data?.expires_at_unix_ms === "number"
                ? data.expires_at_unix_ms
                : (() => {
                    const dt = toDateSafe(data?.expires_at);
                    return dt ? dt.getTime() : null;
                  })();

            return {
              lineId,
              routeId: d.id,
              status: (data?.status as string) ?? null,
              expiresMs,
              updatedMs,
              occupancyLevel: (data?.live?.occupancy_level as OccupancyState) ?? null,
              scheduledDeparture: (data?.scheduled_departure_time as string) ?? null,
            };
          });

          setByLine((prev) => ({ ...prev, [lineId]: rows }));
        },
        (err) => console.error(`❌ routes onSnapshot error line=${lineId}`, err)
      );

      unsubs.push(unsub);
    });

    return () => unsubs.forEach((u) => u());
  }, [lineIds]);

  const routes: RouteColumn[] = useMemo(
    () =>
      lineIds.map((lineId) => ({
        route: lineId,
        lines: (byLine[lineId] ?? [])
          .filter((r) => isVisible(r, nowMs))
          .map((r) => ({
            lineId: r.lineId,
            routeId: r.routeId,
            // Previously "updated recently", which was true of everything shown
            // and so conveyed nothing. Now distinguishes a train reported moving
            // from one that is merely scheduled or silent. Legacy docs fall back
            // to the recency rule to preserve their existing appearance.
            active:
              r.status != null
                ? r.status === "ACTIVE"
                : r.updatedMs != null && nowMs - r.updatedMs < LIVE_MS,
            lastUpdate: r.updatedMs != null ? new Date(r.updatedMs) : null,
            occupancyLevel: r.occupancyLevel,
          })),
      })),
    [lineIds, byLine, nowMs]
  );

  return (
    <React.Fragment>
      {/* Sticky: the scroll container is the MUI drawer paper, and this box is a
          sibling of the grid rather than fixed — so once the columns overflow it
          would otherwise scroll out of view. The columns are 3-per-row, so total
          height is ~3x one column and overflow is now the normal case.
          bgcolor must be opaque (matches the drawer paper) or plates show through.
          Top padding rather than margin: it sits inside the sticky box so the
          background covers the full area, including the strip under the Topbar. */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "#18364a",
          pt: "80px",
          px: "4px",
          pb: "17px",
          color: "#98a1aa",
        }}
      >
        {/* Occupancy colour key */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "25px", marginBottom: "8px" }}>
          {[
            { c: "#6FAC5B", label: "Low" },
            { c: "#FFB600", label: "Med" },
            { c: "#FD0000", label: "High" },
          ].map(({ c, label }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Box sx={{ width: "14px", height: "3px", borderRadius: "2px", backgroundColor: c }} />
              <Typography sx={{ fontSize: "9px", color: "#7c8794" }} noWrap>{label}</Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontSize: "12px", marginLeft: "25px" }} noWrap>
          LIVE TRAINS:
        </Typography>
      </Box>

      <Grid container spacing={0} sx={{ margin: 0 }}>
        {routes.map((routeCol) => (
          <Grid key={routeCol.route} size={4}>
            <Typography sx={{ fontSize: "12px", marginLeft: "15px", color: "#5d6679" }} noWrap>
              {routeCol.route}
            </Typography>

            {routeCol.lines.map((train) => (
              <TrainPlate
                key={`${train.lineId}:${train.routeId}`}
                train={train}
                selected={selected}
                setSelected={setSelected}
              />
            ))}
          </Grid>
        ))}
      </Grid>
    </React.Fragment>
  );
};

export default Sidebar;